import type { IClientInfo } from "@lexicanon/shared"
import { getVersion } from "@tauri-apps/api/app"
import { IS_TAURI } from "./runtime-origin"

/** Coarse OS family from a user-agent string. */
function detectOs(ua: string): string | undefined {
  if (/Mac OS X|Macintosh/i.test(ua)) return "macOS"
  if (/Windows/i.test(ua)) return "Windows"
  if (/iPhone|iPad|iPod/i.test(ua)) return "iOS"
  if (/Android/i.test(ua)) return "Android"
  if (/Linux/i.test(ua)) return "Linux"
  return undefined
}

/** Browser family from a user-agent string (order matters — Edge & Chrome both
 * carry "Chrome", Chrome & Safari both carry "Safari"). */
function detectBrowser(ua: string): string | undefined {
  if (/Edg\//i.test(ua)) return "Edge"
  if (/OPR\/|Opera/i.test(ua)) return "Opera"
  if (/Firefox\//i.test(ua)) return "Firefox"
  if (/Chrome\//i.test(ua)) return "Chrome"
  if (/Safari\//i.test(ua)) return "Safari"
  return undefined
}

/**
 * Build the recording-client descriptor at capture time. The desktop app and a
 * real browser both run the same hosted SPA, so this is what lets the meeting UI
 * say "Desktop app · macOS · v0.1.5" vs "Browser · Safari · macOS". The desktop
 * app version comes from Tauri's `getVersion()`; the browser family is omitted
 * for the desktop (it's an embedded webview, not a user-facing browser).
 */
export async function getClientInfo(): Promise<IClientInfo> {
  const ua = typeof navigator !== "undefined" ? navigator.userAgent : ""
  const os = detectOs(ua)
  if (IS_TAURI) {
    let appVersion: string | undefined
    try {
      appVersion = await getVersion()
    } catch {
      // Pre-0.1.3 shell without the app-version capability — leave undefined.
    }
    return { kind: "desktop", ...(os ? { os } : {}), ...(appVersion ? { appVersion } : {}) }
  }
  const browser = detectBrowser(ua)
  return { kind: "browser", ...(os ? { os } : {}), ...(browser ? { browser } : {}) }
}

/**
 * Human label for the meeting's recording source. Prefers the captured client
 * descriptor; falls back to the legacy `source` string for meetings recorded
 * before client capture landed (or non-streaming sources like Teams).
 */
export function formatClientLabel(clientInfo: IClientInfo | null | undefined, source?: string): string {
  if (!clientInfo) {
    if (source === "browser") return "Browser"
    if (source === "teams") return "Microsoft Teams"
    return source ?? "Unknown"
  }
  const parts =
    clientInfo.kind === "desktop"
      ? ["Desktop app", clientInfo.os, clientInfo.appVersion ? `v${clientInfo.appVersion}` : undefined]
      : ["Browser", clientInfo.browser, clientInfo.os]
  return parts.filter(Boolean).join(" · ")
}
